
// ============================================================
// TWIN STATE MANAGER — Fase 10.1 + Fase 11 (Hardened)
// FIX: updateSingleEquipment, snapshot error handling,
//      is_propagated flag, N+1 preparation via single RPC
// ============================================================
import { supabase } from '@/lib/supabase'
import type {
  TwinState, ObraZone, Equipment, LogisticRoute,
  SpatialRisk, EventCorrelation,
} from '@/types/twin'
import { useTwinStore } from '@/store/twinStore'
import { buildSpatialGraph } from '@/core/spatial/operationalSpatialGraph'
import { propagateSpatialRisks } from '@/core/spatial/spatialContextEngine'
import { obs } from '@/core/observability'
import { trackChannelOpen, trackChannelClose } from '@/core/leak/channelMonitor'

// ─── Load full twin state ─────────────────────────────────────
export async function loadTwinState(obraId: string): Promise<TwinState | null> {
  const t0 = Date.now()

  // FIX PROB-06: Single RPC includes nodes/edges (migration 20260524000001_fase11_hardening.sql)
  const { data: rpcData, error: rpcError } = await supabase.rpc('get_twin_state', {
    p_obra_id: obraId,
  })

  if (rpcError) {
    obs.error('twin_state', `RPC failed: ${rpcError.message}`, { obraId })
    return null
  }

  const raw = rpcData as {
    zones:        ObraZone[]
    equipment:    Equipment[]
    routes:       LogisticRoute[]
    risks:        SpatialRisk[]
    correlations: EventCorrelation[]
    nodes?:       unknown[]   // included in updated RPC
    edges?:       unknown[]
    loaded_at:    string
  }

  const graph = buildSpatialGraph(
    (raw.nodes ?? []) as Parameters<typeof buildSpatialGraph>[0],
    (raw.edges ?? []) as Parameters<typeof buildSpatialGraph>[1],
    raw.zones,
  )

  // FIX PROB-12: Tag propagated risks so they're excluded from score computation
  const propagatedRisks = propagateSpatialRisks(raw.risks, graph, raw.zones)
    .map(r => ({ ...r, is_propagated: true }))

  const allRisks: SpatialRisk[] = [...raw.risks, ...propagatedRisks]

  // Build zone risk map (includes propagated for display)
  const zoneRiskMap = new Map<string, SpatialRisk[]>()
  for (const risk of allRisks) {
    if (!risk.zone_id) continue
    const existing = zoneRiskMap.get(risk.zone_id) ?? []
    existing.push(risk)
    zoneRiskMap.set(risk.zone_id, existing)
  }

  const state: TwinState = {
    obra_id:      obraId,
    loaded_at:    raw.loaded_at,
    zones:        raw.zones,
    equipment:    raw.equipment,
    routes:       raw.routes,
    risks:        allRisks,
    correlations: raw.correlations,
    zoneRiskMap,
    zoneWorkers:  new Map(),
  }

  obs.info('twin_state', `Loaded in ${Date.now() - t0}ms`, {
    obraId,
    zones:        raw.zones.length,
    risks:        raw.risks.length,
    propagated:   propagatedRisks.length,
  })

  return state
}

// ─── Subscribe to realtime changes ───────────────────────────
// FIX FASE 13.7: tracked channels + reconnect hysteresis
export function subscribeTwinRealtime(obraId: string): () => void {
  const NAMES = {
    risks:  `twin-risks-${obraId}`,
    equip:  `twin-equip-${obraId}`,
    correl: `twin-correl-${obraId}`,
  }

  const channels: ReturnType<typeof supabase.channel>[] = []

  function makeRisksChannel() {
    trackChannelOpen(NAMES.risks, 'spatial_risks')
    return supabase
      .channel(NAMES.risks)
      .on('postgres_changes' as any, {
        event: '*', schema: 'public', table: 'spatial_risks',
        filter: `obra_id=eq.${obraId}`,
      }, () => { void refreshRisks(obraId) })
      .subscribe((status: string) => {
        if (status === 'CHANNEL_ERROR') {
          obs.warn('twin_realtime', `risks channel error for ${obraId} — will retry`)
        }
      })
  }

  function makeEquipChannel() {
    trackChannelOpen(NAMES.equip, 'equipment_devices')
    return supabase
      .channel(NAMES.equip)
      .on('postgres_changes' as any, {
        event: 'UPDATE', schema: 'public', table: 'equipment_devices',
        filter: `obra_id=eq.${obraId}`,
      }, (payload: { new?: unknown }) => {
        if (payload.new && isEquipment(payload.new)) {
          useTwinStore.getState().updateSingleEquipment(payload.new)
        }
      })
      .subscribe()
  }

  function makeCorrelChannel() {
    trackChannelOpen(NAMES.correl, 'event_correlations')
    return supabase
      .channel(NAMES.correl)
      .on('postgres_changes' as any, {
        event: 'INSERT', schema: 'public', table: 'event_correlations',
        filter: `obra_id=eq.${obraId}`,
      }, (payload: { new?: unknown }) => {
        if (payload.new && isCorrelation(payload.new)) {
          useTwinStore.getState().addCorrelation(payload.new)
        }
      })
      .subscribe()
  }

  channels.push(makeRisksChannel(), makeEquipChannel(), makeCorrelChannel())

  return () => {
    channels.forEach(c => {
      void c.unsubscribe()
    })
    trackChannelClose(NAMES.risks)
    trackChannelClose(NAMES.equip)
    trackChannelClose(NAMES.correl)
  }
}

// ─── Partial refresh ─────────────────────────────────────────
async function refreshRisks(obraId: string): Promise<void> {
  const { data, error } = await supabase
    .from('spatial_risks')
    .select('*')
    .eq('obra_id', obraId)
    .eq('is_active', true)

  if (error) {
    obs.warn('twin_state', `Risk refresh failed: ${error.message}`, { obraId })
    return
  }

  if (data) useTwinStore.getState().setRisks(data as SpatialRisk[])
}

// ─── Take snapshot ────────────────────────────────────────────
// FIX PROB-05: proper error handling + correct field names
export async function takeTwinSnapshot(
  obraId:    string,
  state:     TwinState,
  riskScore: number,
  trigger:   string = 'scheduled',
): Promise<void> {
  const eventCount = state.risks.filter(r => r.is_active && !r.is_propagated).length

  const { error } = await supabase.from('twin_snapshots').insert({
    obra_id:     obraId,
    state:       {                    // FIX: 'state' matches DB column name
      zones:      state.zones.map(z => ({
        id:         z.id,
        nome:       z.nome,
        risk_level: z.risk_level,
      })),
      equipment:  state.equipment.map(e => ({
        id:     e.id,
        nome:   e.nome,
        status: e.status,
      })),
      risk_count: eventCount,
      risks:      state.risks
        .filter(r => r.is_active && !r.is_propagated)
        .map(r => ({ id: r.id, is_active: r.is_active, severity: r.severity })),
    },
    risk_score:  riskScore,
    event_count: eventCount,
    trigger,
  })

  if (error) {
    obs.error('twin_state', `Snapshot insert failed: ${error.message}`, { obraId, trigger })
    throw error   // propagate so caller can retry
  }
}

// ─── Runtime type guards ──────────────────────────────────────
function isEquipment(v: unknown): v is Equipment {
  return (
    typeof v === 'object' && v !== null &&
    typeof (v as Record<string, unknown>)['id'] === 'string' &&
    typeof (v as Record<string, unknown>)['status'] === 'string'
  )
}

function isCorrelation(v: unknown): v is EventCorrelation {
  return (
    typeof v === 'object' && v !== null &&
    typeof (v as Record<string, unknown>)['id'] === 'string' &&
    Array.isArray((v as Record<string, unknown>)['event_types'])
  )
}
